import React, { useCallback, useEffect, useState } from 'react';
import { FiAlertTriangle, FiCheck, FiSave } from 'react-icons/fi';
import { useApi } from '../hooks/useApi';
import { FormAlert } from './form';
import CurrencyBadge from './CurrencyBadge';
import { formatMoney as formatMoneyCur } from '../utils/money';

const CATEGORIAS = ['Insumos', 'Alquiler', 'Servicios', 'Impuestos', 'Sueldos', 'Otro'];

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

function FxChips({ porMoneda, modoOscuro, textMuted }) {
  const rows = Object.entries(porMoneda || {}).filter(([, r]) => (r.cantidad || 0) > 0 || (r.nativo || 0) !== 0);
  if (!rows.length || (rows.length === 1 && rows[0][0] === 'ARS')) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5">
      {rows.map(([moneda, row]) => (
        <span
          key={moneda}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] tabular-nums ${
            modoOscuro ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-600 border border-gray-200'
          }`}
        >
          <CurrencyBadge currency={moneda} modoOscuro={modoOscuro} size="xs" />
          {formatMoneyCur(row.nativo, moneda, { showCode: moneda !== 'ARS' })}
          {moneda !== 'ARS' && row.ars > 0 && (
            <span className={textMuted}>≈ {formatMoneyCur(row.ars, 'ARS')}</span>
          )}
        </span>
      ))}
    </div>
  );
}

function StatusBadge({ row, alertBadge }) {
  if (!(row.presupuesto > 0)) {
    return <span className="text-[11px] opacity-60">Sin tope</span>;
  }
  if (row.alerta === 'excedido') {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${alertBadge('excedido')}`}>
        <FiAlertTriangle className="w-3 h-3" /> Excedido
      </span>
    );
  }
  if (row.alerta === 'advertencia') {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${alertBadge('advertencia')}`}>
        <FiAlertTriangle className="w-3 h-3" /> 80%+
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${alertBadge(null)}`}>
      <FiCheck className="w-3 h-3" /> OK
    </span>
  );
}

function BudgetPanel({ modoOscuro, box, textPri, textSec, textMuted, btnSec, divide, formatMoney }) {
  const { secureFetch } = useApi();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [rows, setRows] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState({});
  const [scenario, setScenario] = useState('base');

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await secureFetch(`/api/budgets/status?year=${year}&month=${month}&scenario=${scenario}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al cargar presupuestos');
      setRows(data.categorias || []);
      setResumen(data.resumen || null);

      const nextDraft = {};
      for (const cat of CATEGORIAS) {
        const found = (data.categorias || []).find((r) => r.categoria === cat);
        nextDraft[cat] = found?.presupuesto > 0 ? String(found.presupuesto) : '';
      }
      setDraft(nextDraft);
    } catch (err) {
      setError(err.message);
      setRows([]);
      setResumen(null);
    } finally {
      setLoading(false);
    }
  }, [secureFetch, year, month, scenario]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const items = CATEGORIAS
        .map((categoria) => ({
          categoria,
          monto: Number(String(draft[categoria] || '').replace(',', '.')) || 0,
        }))
        .filter((item) => item.monto > 0);

      const res = await secureFetch('/api/budgets', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year, month, scenario, items }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al guardar');
      setSuccess(data.message || 'Presupuesto guardado.');
      setEditMode(false);
      fetchStatus();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const copiarMesAnterior = async () => {
    let y = year;
    let m = month - 1;
    if (m < 1) {
      m = 12;
      y -= 1;
    }
    setError('');
    try {
      const res = await secureFetch(`/api/budgets/status?year=${y}&month=${m}&scenario=${scenario}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'No se pudo copiar el mes anterior');
      const next = {};
      for (const cat of CATEGORIAS) {
        const found = (data.categorias || []).find((r) => r.categoria === cat);
        next[cat] = found?.presupuesto > 0 ? String(found.presupuesto) : '';
      }
      if (!Object.values(next).some(Boolean)) {
        setError(`No hay presupuestos cargados en ${MESES[m - 1]} ${y}.`);
        return;
      }
      setDraft(next);
      setEditMode(true);
      setSuccess(`Copiado el presupuesto de ${MESES[m - 1]} ${y}. Revisá y guardá.`);
    } catch (err) {
      setError(err.message);
    }
  };

  const alertBadge = (alerta) => {
    if (alerta === 'excedido') {
      return modoOscuro
        ? 'bg-red-900/40 text-red-300 border-red-800'
        : 'bg-red-50 text-red-700 border-red-200';
    }
    if (alerta === 'advertencia') {
      return modoOscuro
        ? 'bg-amber-900/30 text-amber-300 border-amber-800'
        : 'bg-amber-50 text-amber-800 border-amber-200';
    }
    return modoOscuro
      ? 'bg-green-900/30 text-green-300 border-green-800'
      : 'bg-green-50 text-green-700 border-green-200';
  };

  const progressColor = (alerta) => {
    if (alerta === 'excedido') return 'bg-red-500';
    if (alerta === 'advertencia') return 'bg-amber-500';
    return 'bg-[#1F80FF]';
  };

  const años = [];
  for (let a = now.getFullYear() - 1; a <= now.getFullYear() + 1; a += 1) años.push(a);

  const hasFx = Object.keys(resumen?.porMoneda || {}).some((m) => m !== 'ARS')
    || rows.some((r) => Object.keys(r.porMoneda || {}).some((m) => m !== 'ARS'));
  const disponible = resumen?.disponible ?? ((resumen?.presupuestoTotal || 0) - (resumen?.gastadoTotal || 0));

  return (
    <div className="space-y-2">
      <FormAlert type="error" className="mb-2">{error}</FormAlert>
      <FormAlert type="success" className="mb-2">{success}</FormAlert>

      <div className={`rounded-2xl border overflow-hidden ${box}`}>
        <div className={`px-3 py-2 border-b space-y-2 ${divide}`}>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <h2 className={`text-sm font-semibold ${textPri}`}>Presupuestos por categoría</h2>
              <p className={`text-[11px] ${textMuted}`}>
                Tope mensual en pesos. Alerta al 80% y si te pasás.
                {hasFx ? ' Los gastos en USD/EUR se convierten con el TC de cada registro.' : ''}
              </p>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <div className={`inline-flex rounded-md border p-0.5 ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
                {[
                  { id: 'base', label: 'Base' },
                  { id: 'optimista', label: 'Optimista' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setScenario(opt.id)}
                    className={`px-2.5 py-1 rounded text-[11px] font-semibold ${
                      scenario === opt.id
                        ? 'bg-[#1F80FF] text-white'
                        : modoOscuro ? 'text-gray-300' : 'text-gray-600'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <select
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                className={`px-2 py-1.5 rounded-md border text-xs ${modoOscuro ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
              >
                {MESES.map((label, idx) => (
                  <option key={label} value={idx + 1}>{label}</option>
                ))}
              </select>
              <select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className={`px-2 py-1.5 rounded-md border text-xs ${modoOscuro ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
              >
                {años.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
              {!editMode ? (
                <>
                  <button
                    type="button"
                    onClick={copiarMesAnterior}
                    className={`px-3 py-1.5 rounded-md border text-xs font-semibold ${btnSec}`}
                  >
                    Copiar mes ant.
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditMode(true)}
                    className="px-3 py-1.5 rounded-md text-xs font-semibold bg-[#1F80FF] text-white"
                  >
                    Editar
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => { setEditMode(false); fetchStatus(); }}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold ${btnSec}`}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-[#1F80FF] text-white disabled:opacity-50"
                  >
                    <FiSave className="w-3.5 h-3.5" />
                    {saving ? 'Guardando…' : 'Guardar'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {resumen && !editMode && (
          <>
            <div className={`grid grid-cols-2 lg:grid-cols-4 gap-px ${modoOscuro ? 'bg-gray-800' : 'bg-gray-100'}`}>
              {[
                { label: 'Presupuesto', value: formatMoney(resumen.presupuestoTotal) },
                { label: 'Gastado', value: formatMoney(resumen.gastadoTotal), hint: hasFx ? 'en pesos' : null },
                {
                  label: 'Disponible',
                  value: formatMoney(disponible),
                  color: disponible < 0
                    ? (modoOscuro ? 'text-red-300' : 'text-red-700')
                    : textPri,
                },
                {
                  label: 'Alertas',
                  value: String(resumen.alertas || 0),
                  hint: resumen.excedidos ? `${resumen.excedidos} excedido${resumen.excedidos === 1 ? '' : 's'}` : null,
                },
              ].map((item) => (
                <div key={item.label} className={`px-3 py-2 min-w-0 ${modoOscuro ? 'bg-gray-900' : 'bg-white'}`}>
                  <p className={`text-[10px] font-semibold uppercase tracking-wide ${textMuted}`}>{item.label}</p>
                  <p className={`text-sm font-semibold tabular-nums break-words ${item.color || textPri}`}>{item.value}</p>
                  {item.hint && <p className={`text-[11px] ${textMuted}`}>{item.hint}</p>}
                </div>
              ))}
            </div>
            {hasFx && (
              <div className={`px-3 py-2 border-t ${divide}`}>
                <p className={`text-[10px] font-semibold uppercase tracking-wide mb-1 ${textMuted}`}>Gastado por moneda</p>
                <FxChips porMoneda={resumen.porMoneda} modoOscuro={modoOscuro} textMuted={textMuted} />
                {resumen.sinCotizacion > 0 && (
                  <p className={`text-[11px] mt-1.5 ${modoOscuro ? 'text-amber-200' : 'text-amber-800'}`}>
                    {resumen.sinCotizacion} gasto(s) en moneda extranjera sin cotización: no entran al consolidado en pesos.
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {loading ? (
          <div className="py-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F80FF] mx-auto mb-2" />
            <p className={`text-xs ${textMuted}`}>Cargando presupuestos…</p>
          </div>
        ) : editMode ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3">
            {CATEGORIAS.map((cat) => {
              const row = rows.find((r) => r.categoria === cat);
              return (
                <label key={cat} className={`block rounded-xl border p-2.5 ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-xs font-medium ${textSec}`}>{cat}</span>
                    {row?.gastado > 0 && (
                      <span className={`text-[10px] tabular-nums ${textMuted}`}>
                        Gastado {formatMoney(row.gastado)}
                      </span>
                    )}
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={draft[cat] || ''}
                    onChange={(e) => setDraft((p) => ({ ...p, [cat]: e.target.value }))}
                    placeholder="Tope en pesos"
                    className={`mt-1.5 w-full px-3 py-1.5 rounded-lg border text-sm ${modoOscuro ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
                  />
                </label>
              );
            })}
          </div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <p className={`text-sm ${textSec}`}>Todavía no definiste presupuestos para {MESES[month - 1]} {year}.</p>
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              <button
                type="button"
                onClick={copiarMesAnterior}
                className={`px-3 py-1.5 rounded-md border text-xs font-semibold ${btnSec}`}
              >
                Copiar mes anterior
              </button>
              <button
                type="button"
                onClick={() => setEditMode(true)}
                className="px-3 py-1.5 rounded-md text-xs font-semibold bg-[#1F80FF] text-white"
              >
                Configurar presupuesto
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-2 p-3 md:hidden">
              {rows.map((row) => {
                const pct = row.porcentaje != null ? Math.min(row.porcentaje * 100, 999) : null;
                const resto = row.disponible != null ? row.disponible : (row.presupuesto || 0) - (row.gastado || 0);
                return (
                  <div key={row.categoria} className={`rounded-xl border p-3 ${modoOscuro ? 'border-gray-800 bg-gray-800/40' : 'border-gray-100 bg-gray-50'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm font-semibold ${textPri}`}>{row.categoria}</p>
                      <StatusBadge row={row} alertBadge={alertBadge} />
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      <div>
                        <p className={`text-[10px] uppercase font-semibold ${textMuted}`}>Tope</p>
                        <p className={`text-xs font-semibold tabular-nums ${textPri}`}>{row.presupuesto > 0 ? formatMoney(row.presupuesto) : '—'}</p>
                      </div>
                      <div>
                        <p className={`text-[10px] uppercase font-semibold ${textMuted}`}>Gastado</p>
                        <p className={`text-xs font-semibold tabular-nums ${textPri}`}>{formatMoney(row.gastado)}</p>
                      </div>
                      <div>
                        <p className={`text-[10px] uppercase font-semibold ${textMuted}`}>Resta</p>
                        <p className={`text-xs font-semibold tabular-nums ${
                          resto < 0
                            ? modoOscuro ? 'text-red-300' : 'text-red-700'
                            : textPri
                        }`}>
                          {row.presupuesto > 0 ? formatMoney(resto) : '—'}
                        </p>
                      </div>
                    </div>
                    <FxChips porMoneda={row.porMoneda} modoOscuro={modoOscuro} textMuted={textMuted} />
                    {row.presupuesto > 0 && (
                      <div className="mt-2">
                        <div className={`h-1.5 rounded-full overflow-hidden ${modoOscuro ? 'bg-gray-700' : 'bg-gray-200'}`}>
                          <div className={`h-full rounded-full ${progressColor(row.alerta)}`} style={{ width: `${Math.min(pct || 0, 100)}%` }} />
                        </div>
                        <p className={`text-[11px] mt-1 ${textMuted}`}>{pct != null ? `${pct.toFixed(0)}% usado` : ''}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`text-[11px] uppercase tracking-wide ${textMuted} ${modoOscuro ? 'bg-gray-800/60' : 'bg-gray-50'}`}>
                    <th className="text-left font-semibold py-2.5 px-4">Categoría</th>
                    <th className="text-right font-semibold py-2.5 px-3">Tope</th>
                    <th className="text-right font-semibold py-2.5 px-3">Gastado</th>
                    <th className="text-right font-semibold py-2.5 px-3">Resta</th>
                    <th className="text-right font-semibold py-2.5 px-3">Uso</th>
                    <th className="text-center font-semibold py-2.5 px-4">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const pct = row.porcentaje != null ? Math.min(row.porcentaje * 100, 999) : null;
                    const resto = row.disponible != null ? row.disponible : (row.presupuesto || 0) - (row.gastado || 0);
                    return (
                      <tr key={row.categoria} className={`border-t ${modoOscuro ? 'border-gray-800' : 'border-gray-100'}`}>
                        <td className={`py-2.5 px-4 text-sm font-medium ${textPri}`}>
                          {row.categoria}
                          <FxChips porMoneda={row.porMoneda} modoOscuro={modoOscuro} textMuted={textMuted} />
                        </td>
                        <td className={`py-2.5 px-3 text-sm text-right tabular-nums ${textSec}`}>
                          {row.presupuesto > 0 ? formatMoney(row.presupuesto) : '—'}
                        </td>
                        <td className={`py-2.5 px-3 text-sm text-right tabular-nums ${textSec}`}>{formatMoney(row.gastado)}</td>
                        <td className={`py-2.5 px-3 text-sm text-right tabular-nums ${
                          row.presupuesto > 0 && resto < 0
                            ? modoOscuro ? 'text-red-300' : 'text-red-700'
                            : textSec
                        }`}>
                          {row.presupuesto > 0 ? formatMoney(resto) : '—'}
                        </td>
                        <td className="py-2.5 px-3">
                          {row.presupuesto > 0 ? (
                            <div className="flex items-center gap-2 justify-end">
                              <div className={`w-24 h-2 rounded-full overflow-hidden ${modoOscuro ? 'bg-gray-700' : 'bg-gray-200'}`}>
                                <div
                                  className={`h-full rounded-full ${progressColor(row.alerta)}`}
                                  style={{ width: `${Math.min(pct, 100)}%` }}
                                />
                              </div>
                              <span className={`text-xs tabular-nums ${textMuted}`}>{pct != null ? `${pct.toFixed(0)}%` : '—'}</span>
                            </div>
                          ) : (
                            <span className={`block text-right text-xs ${textMuted}`}>Sin tope</span>
                          )}
                        </td>
                        <td className="py-2.5 px-4 text-center">
                          <StatusBadge row={row} alertBadge={alertBadge} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default BudgetPanel;
